// src/components/AdminFeedback.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from '../i18n/i18n';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Avatar,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import API_ENDPOINTS from '../config/apiConfig';
import FEEDBACK_BADGES from '../config/feedbackBadges';

// ── badge lookup ──────────────────────────────────────────────────────────────
const BADGE_MAP = Object.fromEntries(FEEDBACK_BADGES.map(b => [b.id, b]));

// ── helpers ───────────────────────────────────────────────────────────────────
const LANG_TO_LOCALE = { spanish: 'es', english: 'en' };

function formatDate(dateStr, lang) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const locale = LANG_TO_LOCALE[lang] || lang || 'en';
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function BadgeIcon({ icon, size = 22 }) {
  if (!icon) return null;
  if (typeof icon === 'string' && icon.startsWith('/')) {
    return (
      <img
        src={icon}
        alt="badge"
        style={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
      />
    );
  }
  return <span style={{ fontSize: size, lineHeight: 1 }}>{icon}</span>;
}

// ── UserPill: clickable name chip, highlighted when it matches the filter ─────
function UserPill({ name, email, isDeleted, isHighlighted, onClick, t }) {
  const displayName = name || email || '—';
  const label = isDeleted ? `${displayName} (${t('feedback_deleted_user')})` : displayName;

  return (
    <Chip
      size="small"
      icon={
        <Avatar sx={{ width: 20, height: 20, bgcolor: isHighlighted ? '#1B4965' : '#62B6CB' }}>
          <PersonIcon sx={{ fontSize: 13 }} />
        </Avatar>
      }
      label={label}
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        fontWeight: isHighlighted ? 700 : 500,
        bgcolor: isHighlighted ? '#FFF3CD' : 'transparent',
        border: isHighlighted ? '1.5px solid #F59E0B' : '1px solid #cce3ec',
        color: isHighlighted ? '#92400E' : '#1B4965',
        '&:hover': {
          bgcolor: isHighlighted ? '#FDE68A' : '#e8f4f8',
        },
        transition: 'all 0.15s ease',
        maxWidth: 200,
      }}
    />
  );
}

// ── AdminFeedbackCard ─────────────────────────────────────────────────────────
function AdminFeedbackCard({ entry, filteredUserId, onFilterUser, t, lang }) {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const badge = entry.badge_id ? BADGE_MAP[entry.badge_id] : null;

  // Determine if giver/receiver match the current filter (by ObjectID string)
  const fromId = entry.from_user_id?.$oid || String(entry.from_user_id || '');
  const toId = entry.to_user_id?.$oid || String(entry.to_user_id || '');
  const fromHighlighted = filteredUserId && fromId === filteredUserId;
  const toHighlighted = filteredUserId && toId === filteredUserId;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        borderColor: (fromHighlighted || toHighlighted) ? '#F59E0B' : '#e0e0e0',
        boxShadow: (fromHighlighted || toHighlighted) ? '0 0 0 2px rgba(245,158,11,0.18)' : 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
    >
      <CardContent sx={{ pb: '12px !important' }}>
        {/* ── Giver → Receiver row ─────────────────────────────────────────── */}
        <Stack
          direction={isSmDown ? 'column' : 'row'}
          alignItems={isSmDown ? 'flex-start' : 'center'}
          spacing={isSmDown ? 1 : 1.5}
          sx={{ mb: 1.5 }}
        >
          {/* FROM */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: '#888', fontWeight: 500, mb: 0.3, textTransform: 'uppercase', fontSize: '0.65rem' }}>
              {t('feedback_from')}
            </Typography>
            <UserPill
              name={entry.from_name}
              email={entry.from_email}
              isDeleted={entry.from_deleted}
              isHighlighted={fromHighlighted}
              onClick={() => onFilterUser({ id: fromId, name: entry.from_name, email: entry.from_email })}
              t={t}
            />
          </Box>

          {/* Arrow */}
          <ArrowForwardIcon
            sx={{
              color: '#62B6CB',
              fontSize: isSmDown ? 16 : 22,
              flexShrink: 0,
              mt: isSmDown ? 0 : 2.5,
              alignSelf: isSmDown ? 'center' : 'flex-end',
              mb: isSmDown ? 0 : 0.4,
            }}
          />

          {/* TO */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: '#888', fontWeight: 500, mb: 0.3, textTransform: 'uppercase', fontSize: '0.65rem' }}>
              {t('feedback_to')}
            </Typography>
            <UserPill
              name={entry.to_name}
              email={entry.to_email}
              isDeleted={entry.to_deleted}
              isHighlighted={toHighlighted}
              onClick={() => onFilterUser({ id: toId, name: entry.to_name, email: entry.to_email })}
              t={t}
            />
          </Box>

          {/* Badge – pushed to the right on md+ */}
          {badge && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                ml: isSmDown ? 0 : 'auto !important',
                bgcolor: '#e8f4f8',
                borderRadius: 2,
                px: 1,
                py: 0.5,
                flexShrink: 0,
              }}
            >
              <BadgeIcon icon={badge.icon} size={24} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1B4965' }}>
                {t(`feedback_badge_${badge.id}_name`)}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* ── Message ──────────────────────────────────────────────────────── */}
        <Typography
          variant="body2"
          sx={{ color: '#333', whiteSpace: 'pre-wrap', mb: 1, pl: 0.5 }}
        >
          {entry.message}
        </Typography>

        {/* ── Date ─────────────────────────────────────────────────────────── */}
        <Typography variant="caption" sx={{ color: '#aaa' }}>
          {formatDate(entry.submitted_at?.$date || entry.submitted_at, lang)}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── Main AdminFeedback component ──────────────────────────────────────────────
export default function AdminFeedback() {
  const { t, lang } = useTranslation();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [allFeedback, setAllFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Active filter: { id: string (ObjectId), name: string, email: string } | null
  const [filterUser, setFilterUser] = useState(null);

  // ── load all feedback ─────────────────────────────────────────────────────
  const loadFeedback = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    fetch(API_ENDPOINTS.FEEDBACK_ADMIN_ALL, { credentials: 'include' })
      .then(res => (res.ok ? res.json() : Promise.reject()))
      .then(data => setAllFeedback(Array.isArray(data) ? data : []))
      .catch(() => { setAllFeedback([]); setLoadError(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadFeedback(); }, [loadFeedback]);

  // ── derive unique user list for autocomplete ──────────────────────────────
  const userOptions = useMemo(() => {
    const map = new Map();
    allFeedback.forEach(entry => {
      const fromId = entry.from_user_id?.$oid || String(entry.from_user_id || '');
      const toId = entry.to_user_id?.$oid || String(entry.to_user_id || '');
      if (fromId && !map.has(fromId)) {
        map.set(fromId, { id: fromId, name: entry.from_name || '', email: entry.from_email || '' });
      }
      if (toId && !map.has(toId)) {
        map.set(toId, { id: toId, name: entry.to_name || '', email: entry.to_email || '' });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
  }, [allFeedback]);

  // ── filtered list ─────────────────────────────────────────────────────────
  const displayedFeedback = useMemo(() => {
    if (!filterUser) return allFeedback;
    return allFeedback.filter(entry => {
      const fromId = entry.from_user_id?.$oid || String(entry.from_user_id || '');
      const toId = entry.to_user_id?.$oid || String(entry.to_user_id || '');
      return fromId === filterUser.id || toId === filterUser.id;
    });
  }, [allFeedback, filterUser]);

  const handleFilterUser = (user) => {
    // Clicking the already-filtered user clears the filter
    setFilterUser(prev => (prev?.id === user.id ? null : user));
  };

  const clearFilter = () => setFilterUser(null);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: { xs: 3, sm: 4 } }} flexWrap="wrap" gap={1}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {t('admin_feedback_title')}
        </Typography>
        <Tooltip title={t('admin_feedback_refresh')}>
          <span>
            <IconButton onClick={loadFeedback} disabled={loading} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {/* ── Search / filter bar ────────────────────────────────────────────── */}
      <Stack direction={isSmDown ? 'column' : 'row'} spacing={2} alignItems={isSmDown ? 'stretch' : 'center'} sx={{ mb: 3 }}>
        <Autocomplete
          options={userOptions}
          getOptionLabel={(opt) => opt.name ? `${opt.name} (${opt.email})` : opt.email}
          value={filterUser}
          onChange={(_, val) => setFilterUser(val)}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('admin_feedback_search_label')}
              placeholder={t('admin_feedback_search_placeholder')}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <FilterListIcon fontSize="small" sx={{ color: '#62B6CB', mr: 0.5 }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ minWidth: 280, flex: 1, maxWidth: 420 }}
          clearOnEscape
        />

        {/* Active filter chip */}
        {filterUser && (
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmojiEventsIcon sx={{ fontSize: 14 }} />
                <span>
                  {t('admin_feedback_filtering_by')}{' '}
                  <strong>{filterUser.name || filterUser.email}</strong>
                  {' — '}{displayedFeedback.length}{' '}
                  {displayedFeedback.length === 1 ? t('admin_feedback_result') : t('admin_feedback_results')}
                </span>
              </Box>
            }
            onDelete={clearFilter}
            deleteIcon={<CloseIcon fontSize="small" />}
            sx={{
              bgcolor: '#FFF3CD',
              border: '1.5px solid #F59E0B',
              color: '#92400E',
              fontWeight: 500,
              '& .MuiChip-deleteIcon': { color: '#92400E' },
            }}
          />
        )}
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* ── Legend ─────────────────────────────────────────────────────────── */}
      {!loading && allFeedback.length > 0 && (
        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
          {t('admin_feedback_click_hint')}
        </Typography>
      )}

      {/* ── Content ────────────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {t('admin_feedback_load_error')}
        </Alert>
      ) : allFeedback.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {t('admin_feedback_empty')}
        </Alert>
      ) : displayedFeedback.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {t('admin_feedback_no_results')}
        </Alert>
      ) : (
        <Box>
          {!filterUser && (
            <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
              {allFeedback.length === 1
                ? t('admin_feedback_total_one')
                : `${allFeedback.length} ${t('admin_feedback_total_many')}`}
            </Typography>
          )}
          {displayedFeedback.map((entry, idx) => (
            <AdminFeedbackCard
              key={idx}
              entry={entry}
              filteredUserId={filterUser?.id || null}
              onFilterUser={handleFilterUser}
              t={t}
              lang={lang}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
